import type { CurrentUser } from '@/services/types';

export default function access(initialState: { currentUser?: CurrentUser }) {
  const { currentUser } = initialState || {};
  const permissions = currentUser?.permissions || [];
  const roles = currentUser?.roles || [];

  // 账号是否被禁用（status=0）：禁用后所有权限置空，页面走 403
  const isDisabled = currentUser?.status === 0;

  // 是否管理员（管理员拥有全部权限）
  const isAdmin = !isDisabled && roles.includes('ADMIN');

  // 是否评审人员
  const isReviewer = !isDisabled && roles.includes('REVIEWER');

  // 本代理商团队读取范围，与后端业务范围判断一致；只有 ADMIN 可跨代理商。
  const isFullDataRole = !isDisabled && (isAdmin || roles.includes('SALES_MANAGER') || roles.includes('PROJECT_MANAGER') || roles.includes('AGENT_MANAGER'));

  // 辅助函数：检查是否有权限
  const hasPermission = (permission: string) => {
    return !isDisabled && (isAdmin || permissions.includes(permission));
  };

  return {
    // 账号被禁用（供移动端等复用）
    isDisabled,

    // 当前登录用户ID（供"只看/只改自己负责"的判断复用）
    currentUserId: currentUser?.id,

    // 是否管理员
    canAdmin: isAdmin,

    // Actor may select another eligible owner; every action and agency still needs authorization.
    canAssignBusinessOwner: !isDisabled && (isAdmin || roles.includes('SALES_MANAGER') || roles.includes('AGENT_MANAGER')),

    // 工作台 - 所有登录用户都能访问
    canDashboard: !isDisabled,

    // 线索权限
    canLeadList: hasPermission('lead:list'),
    canLeadApplication: hasPermission('lead:application'),
    canLeadApplicationCreate: hasPermission('lead:application:create'),
    canLeadApplicationUpdate: hasPermission('lead:application:update'),
    canLeadApplicationDelete: hasPermission('lead:application:delete'),
    canLeadApplicationAssign: hasPermission('lead:application:assign'),
    canLeadCreate: hasPermission('lead:create'),
    canLeadUpdate: hasPermission('lead:update'),
    canLeadDelete: hasPermission('lead:delete'),
    canLeadReview: hasPermission('lead:review'),
    canLeadConvert: hasPermission('lead:convert'),
    canLeadApprove: hasPermission('lead:approve'),
    canLeadAssign: hasPermission('lead:assign'),
    canLeadSourceConfig: hasPermission('lead:source:config'),

    // 其他留资信息权限（三类别）
    // 管理权限：可查看 + 编辑/删除
    canOtherLeadMedia: hasPermission('otherLead:media'),
    canOtherLeadBusiness: hasPermission('otherLead:business'),
    canOtherLeadSupplier: hasPermission('otherLead:supplier'),
    // 只读权限：仅查看
    canOtherLeadMediaView: hasPermission('otherLead:media:view'),
    canOtherLeadBusinessView: hasPermission('otherLead:business:view'),
    canOtherLeadSupplierView: hasPermission('otherLead:supplier:view'),
    canOtherLeadList:
      isAdmin ||
      hasPermission('otherLead:media') ||
      hasPermission('otherLead:business') ||
      hasPermission('otherLead:supplier') ||
      hasPermission('otherLead:media:view') ||
      hasPermission('otherLead:business:view') ||
      hasPermission('otherLead:supplier:view'),
    // 线索管理菜单组：有线索列表或任一留资权限（管理/只读）即可见（其他留资信息挂在线索管理下）
    canLeadGroup:
      hasPermission('lead:list') ||
      hasPermission('lead:application') ||
      isAdmin ||
      hasPermission('otherLead:media') ||
      hasPermission('otherLead:business') ||
      hasPermission('otherLead:supplier') ||
      hasPermission('otherLead:media:view') ||
      hasPermission('otherLead:business:view') ||
      hasPermission('otherLead:supplier:view'),

    // 只用于列表读取范围；不授予创建、修改或分配动作。
    canReadAllCustomers: isFullDataRole || hasPermission('customer:view_all'),
    canReadAllContacts: isFullDataRole || hasPermission('contact:view_all'),
    canReadAllChances: isFullDataRole || hasPermission('salesChance:view_all'),
    canReadAllOpportunities: isFullDataRole || hasPermission('opportunity:view_all'),
    canReadAllVisits: isFullDataRole,

    // 客户权限
    canCustomerList: hasPermission('customer:list'),
    // 客户管理菜单组：有客户列表即可见
    canCustomerGroup: hasPermission('customer:list') || isAdmin,
    canCustomerCreate: hasPermission('customer:create'),
    canCustomerUpdate: hasPermission('customer:update'),
    canCustomerTransferAgency: hasPermission('customer:update') && hasPermission('customer:transfer_agency'),
    canCustomerDelete: hasPermission('customer:delete'),
    canCustomerDetail: hasPermission('customer:detail'),
    canCustomerFollow: hasPermission('customer:follow'),
    canCustomerConvert: hasPermission('customer:convert'),
    canScoreConfig: hasPermission('score:config'),
    // 客户评分权限
    canCustomerScoreView: hasPermission('customer:score:view') || hasPermission('customer:detail'),
    canCustomerScoreCreate: hasPermission('customer:score:create'),
    canCustomerScoreDelete: hasPermission('customer:score:delete'),

    // 销售机会（商机）评分权限：仅控制"发起/修改评分"动作
    canSalesChanceScoreCreate: hasPermission('salesChance:score:create'),

    // 销售机会权限
    canSalesChanceList: hasPermission('salesChance:list'),
    canSalesChanceCreate: hasPermission('salesChance:create'),
    canSalesChanceUpdate: hasPermission('salesChance:update'),
    canSalesChanceDelete: hasPermission('salesChance:delete'),
    canSalesChanceDetail: hasPermission('salesChance:detail'),
    canSalesChanceReview: hasPermission('salesChance:review'),
    canSalesChanceConvert: hasPermission('salesChance:convert'),
    canSalesChanceTrack: hasPermission('salesChance:track'),

    // 评审人员角色
    isReviewer,

    // 商机权限
    canOpportunityList: hasPermission('opportunity:list'),
    canOpportunityCreate: hasPermission('opportunity:create'),
    canOpportunityUpdate: hasPermission('opportunity:update'),
    canOpportunityDelete: hasPermission('opportunity:delete'),
    canOpportunityDetail: hasPermission('opportunity:detail'),
    canOpportunityStage: hasPermission('opportunity:stage'),
    canOpportunityTrack: hasPermission('opportunity:track'),
    canOpportunityWin: hasPermission('opportunity:win'),
    canOpportunityLose: hasPermission('opportunity:lose'),

    // 拜访权限
    canVisitGroup: hasPermission('visit:plan') || hasPermission('visit:record'),
    canVisitPlan: hasPermission('visit:plan'),
    canVisitRecord: hasPermission('visit:record'),
    canVisitCreate: hasPermission('visit:create'),
    canVisitUpdate: hasPermission('visit:update'),
    canVisitDelete: hasPermission('visit:delete'),
    canVisitStart: hasPermission('visit:start'),
    canVisitComplete: hasPermission('visit:complete'),

    // 产品权限
    canProductList: hasPermission('product:list'),
    canProductCreate: hasPermission('product:create'),
    canProductUpdate: hasPermission('product:update'),
    canProductDelete: hasPermission('product:delete'),

    // 代理商权限
    canSupplierList: hasPermission('supplier:list'),
    canSupplierCreate: hasPermission('supplier:create'),
    canSupplierUpdate: hasPermission('supplier:update'),
    canSupplierDelete: hasPermission('supplier:delete'),
    canSupplierDetail: hasPermission('supplier:detail'),

    // 系统管理权限
    canSystemMenu: hasPermission('system:user') || hasPermission('system:role') || hasPermission('system:permission') || isAdmin,
    canScoreMenu: hasPermission('score:config') || isAdmin,
    canSystemUser: hasPermission('system:user'),
    canSystemRole: hasPermission('system:role'),
    canSystemPermission: hasPermission('system:permission'),
    canSystemNotification: isAdmin,

    // 数据分析权限
    canAnalysis: hasPermission('dashboard:view') || hasPermission('dashboard:view_all'),
    canAnalysisViewAll: !isDisabled && (isFullDataRole || permissions.includes('dashboard:view_all')),
  };
}
